import React, { useState } from 'react';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import {
  Activity,
  TrendingUp,
  Clock,
  Download,
  Eye,
  MousePointer,
  Zap,
  Target,
  Users
} from 'lucide-react';

const PerformanceAnalytics = () => {
  const { analytics, exportAnalytics } = useAnalytics();
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedMetric, setSelectedMetric] = useState('overview');

  const performance = analytics.performance || {};
  const pageViews = performance.pageViews || [];
  const conversionRates = performance.conversionRates || {};

  const handleExport = () => {
    const result = exportAnalytics('csv');
    if (result.success) {
      alert('Données de performance exportées avec succès !');
    } else {
      alert('Erreur lors de l\'export');
    }
  };

  const getTotalPageViews = () => {
    return pageViews.reduce((sum, page) => sum + page.views, 0);
  };

  const getTotalUniqueViews = () => {
    return pageViews.reduce((sum, page) => sum + page.uniqueViews, 0);
  };

  const getBounceRateStatus = () => {
    const rate = performance.bounceRate || 0;
    if (rate < 30) return { status: 'excellent', color: 'green' };
    if (rate < 50) return { status: 'bon', color: 'yellow' };
    return { status: 'à améliorer', color: 'red' };
  };

  const getSessionDurationStatus = () => {
    const duration = performance.averageSessionDuration || '0m 0s';
    const minutes = parseInt(duration.split('m')[0]);
    if (minutes > 10) return { status: 'excellent', color: 'green' };
    if (minutes > 5) return { status: 'bon', color: 'yellow' };
    return { status: 'à améliorer', color: 'red' };
  };

  return (
    <div className="space-y-6">
      {/* Header avec contrôles */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics de Performance</h2>
          <p className="text-gray-600">Métriques techniques et comportementales de la plateforme</p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
            <option value="year">Cette année</option>
          </select>
          
          <select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="overview">Vue d'ensemble</option>
            <option value="pages">Pages</option>
            <option value="conversion">Conversion</option>
          </select>
          
          <button
            onClick={handleExport}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
          >
            <Download className="w-4 h-4" />
            Exporter
          </button>
        </div>
      </div>

      {/* Métriques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Vues Totales</p>
              <p className="text-3xl font-bold text-gray-900">{getTotalPageViews().toLocaleString()}</p>
              <p className="text-sm text-gray-500">Toutes pages</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Eye className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Vues Uniques</p>
              <p className="text-3xl font-bold text-gray-900">{getTotalUniqueViews().toLocaleString()}</p>
              <p className="text-sm text-gray-500">Utilisateurs uniques</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Taux de Rebond</p>
              <p className="text-3xl font-bold text-gray-900">{performance.bounceRate || 0}%</p>
              <p className={`text-sm ${getBounceRateStatus().color === 'green' ? 'text-green-600' : 
                getBounceRateStatus().color === 'yellow' ? 'text-yellow-600' : 'text-red-600'}`}>
                {getBounceRateStatus().status}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <MousePointer className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Durée Session</p>
              <p className="text-3xl font-bold text-gray-900">{performance.averageSessionDuration || '0m 0s'}</p>
              <p className={`text-sm ${getSessionDurationStatus().color === 'green' ? 'text-green-600' : 
                getSessionDurationStatus().color === 'yellow' ? 'text-yellow-600' : 'text-red-600'}`}>
                {getSessionDurationStatus().status}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Performance des pages */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance des Pages</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Page
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vues Totales
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vues Uniques
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Taux de Rebond
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Temps Moyen
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pageViews.map((page, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center mr-3">
                        <Eye className="w-4 h-4 text-white" />
                      </div>
                      <div className="text-sm font-medium text-gray-900">{page.page}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{page.views.toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{page.uniqueViews.toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {Math.round((1 - page.uniqueViews / page.views) * 100)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">2m 34s</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Métriques de conversion */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Taux de Conversion</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Target className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Visiteur → Étudiant</h4>
                  <p className="text-sm text-gray-600">Inscription sur la plateforme</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-gray-900">{conversionRates.visitorToStudent || 0}%</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Étudiant → Payant</h4>
                  <p className="text-sm text-gray-600">Passage à un abonnement payant</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-gray-900">{conversionRates.studentToPaid || 0}%</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Cours → Completion</h4>
                  <p className="text-sm text-gray-600">Terminaison d'un cours</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-gray-900">{conversionRates.courseToCompletion || 0}%</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Target className="w-4 h-4 text-yellow-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Completion → Certificat</h4>
                  <p className="text-sm text-gray-600">Obtention d'un certificat</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-gray-900">{conversionRates.completionToCertificate || 0}%</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Métriques d'Engagement</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Pages par session</span>
              <span className="font-semibold text-gray-900">{performance.pagesPerSession || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Durée moyenne par page</span>
              <span className="font-semibold text-gray-900">1m 23s</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Taux de retour</span>
              <span className="font-semibold text-gray-900">67.3%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Fréquence des visites</span>
              <span className="font-semibold text-gray-900">3.2/semaine</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Temps sur site</span>
              <span className="font-semibold text-gray-900">{performance.averageSessionDuration || '0m 0s'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Taux de rebond</span>
              <span className="font-semibold text-gray-900">{performance.bounceRate || 0}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recommandations d'amélioration */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommandations d'Amélioration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-900">Optimiser le Taux de Rebond</h4>
            </div>
            <p className="text-sm text-gray-600">
              Le taux de rebond de {performance.bounceRate || 0}% peut être amélioré en optimisant le contenu des pages d'accueil.
            </p>
          </div>

          <div className="p-4 bg-green-50 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-900">Augmenter la Durée de Session</h4>
            </div>
            <p className="text-sm text-gray-600">
              Améliorer l'engagement avec du contenu interactif et des recommandations personnalisées.
            </p>
          </div>

          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <Target className="w-4 h-4 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-900">Améliorer les Conversions</h4>
            </div>
            <p className="text-sm text-gray-600">
              Optimiser les parcours d'inscription et les appels à l'action pour augmenter les conversions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceAnalytics;
